import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://argostellus.com';
const DEFAULT_IMAGE = `${SITE_URL}/argos-logo.png`;

interface SeoProps {
  /** Full page title, e.g. "Polygon — Plume Forecaster | Argos Tellus". */
  title: string;
  /** Meta description. Clamped to ~160 chars for search-result display. */
  description: string;
  /** Route path beginning with "/", used for canonical + og:url. */
  path: string;
  /** Optional og:image path (absolute or site-relative). */
  image?: string;
  /** Optional schema.org structured data object. */
  jsonLd?: object;
}

// Search engines truncate descriptions past ~160 chars — clamp at a word
// boundary so the snippet never ends mid-word.
function clampDescription(text: string, max = 160): string {
  if (text.length <= max) return text;
  const cut = text.slice(0, max);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > 0 ? cut.slice(0, lastSpace) : cut).replace(/[\s,;:—-]+$/, '')}…`;
}

// Per-page <head> tags. The landing's tags live statically in index.html;
// this component overrides them per route for search + social crawlers.
export function Seo({ title, description, path, image, jsonLd }: SeoProps) {
  const url = `${SITE_URL}${path}`;
  const img = image
    ? image.startsWith('http')
      ? image
      : `${SITE_URL}${image}`
    : DEFAULT_IMAGE;
  const desc = clampDescription(description);

  return (
    <Helmet prioritizeSeoTags>
      <title>{title}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={desc} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />
      {jsonLd ? (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      ) : null}
    </Helmet>
  );
}
